import React from 'react';
import PropTypes from 'prop-types';
import { useWebsocketStore } from '../../../../../stores/websocketStore/websocketProvider';
import { observer } from 'mobx-react-lite';
import { Body4 } from '../../../../atomic/text/Text';
import { colors } from '../../../../../colorVars';
import ApplyRounding from '../../../../../utils/math/ApplyRounding';
import determineSide from '../../../../../utils/positions/DetermineSide';
import { timeFormat } from 'd3-time-format';
import { isOrderOpen } from '../../../../../stores/websocketStore/utils/mungingUtils/orderStatusFilter';
import { CancelOrderButton } from '../../../../atomic/buttons/cancelOrderButton/CancelOrderButton';
import { cancelOrder } from '../../../../../stores/websocketStore/utils/outgoingMessages/orders/cancelOrder';
import { EditableTableField } from '../../../../atomic/inputs/editableTableField/EditableTableField';
import { modifyOpenOrderLimitPrice } from '../../../../../stores/websocketStore/utils/outgoingMessages/orders/modifyOpenOrderLimitPrice';
import { modifyOpenOrderQuantity } from '../../../../../stores/websocketStore/utils/outgoingMessages/orders/modifyOpenOrderQuantity';
import { translateOptionSymbol } from '../../../../../stores/websocketStore/utils/mungingUtils/translateOptionSymbol';

export const OrderTableRow = observer(({ orderId, department, odd }) => {
  const { tradePlanStore } = useWebsocketStore();
  const order = tradePlanStore.orders[department][orderId] ?? null;
  const formatTime = timeFormat('%H:%M %m/%e');
  const updateLimitPrice = (newValue) => {
    modifyOpenOrderLimitPrice(order, newValue);
  };
  const updateQuantity = (newValue) => {
    modifyOpenOrderQuantity(order, newValue);
  };
  if (!order) return null;
  return (
    <>
      {order && (
        <tr
          style={{
            backgroundColor: order.isFlattening && isOrderOpen(order.status, 'TRADIER')
              ? colors.red
              : odd
                ? colors.grey
                : colors.black,
          }}
        >
          <td>
            {isOrderOpen(order.status, 'TRADIER') ? (
              <CancelOrderButton
                width={16}
                clickHandler={() => cancelOrder(order)}
              />
            ) : null}
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {order.class === 'equity'
                ? order.symbol
                : translateOptionSymbol(order.symbol)}
            </Body4>
          </td>
          <td>
            <Body4
              fontWeight={700}
              color={
                isOrderOpen(order.status, 'TRADIER')
                  ? colors.green
                  : colors.blue
              }
            >
              {order.status}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {order.type}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {determineSide(order.side)}
            </Body4>
          </td>
          <td>
            {isOrderOpen(order.status, 'TRADIER') ? (
              <EditableTableField
                bodySize={4}
                initialValue={order.quantity}
                updateValue={updateQuantity}
                backgroundColor={odd ? colors.grey : colors.black}
                color={colors.lightGrey}
                inputType="number"
              />
            ) : (
              <Body4 fontWeight={600} color={colors.lightGrey}>
                {order.quantity}
              </Body4>
            )}
          </td>

          <td>
            {isOrderOpen(order.status, 'TRADIER') ? (
              <EditableTableField
                initialValue={ApplyRounding(order.price, 2)}
                updateValue={updateLimitPrice}
                backgroundColor={odd ? colors.grey : colors.black}
                color={colors.lightGrey}
                inputType="number"
              />
            ) : (
              <Body4 fontWeight={600} color={colors.lightGrey}>
                {ApplyRounding(order.price, 2)}
              </Body4>
            )}
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {ApplyRounding(order.avg_fill_price, 2)}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {order.exec_quantity}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {ApplyRounding(order.stop, 2)}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {ApplyRounding(order.last_fill_price, 2)}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {order.last_fill_quantity}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {order.remaining_quantity}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {order.duration}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {formatTime(new Date(order.create_date))}
            </Body4>
          </td>
          <td>
            <Body4 fontWeight={600} color={colors.lightGrey}>
              {order.reason_description}
            </Body4>
          </td>
        </tr>
      )}
    </>
  );
});

OrderTableRow.propTypes = {
  department: PropTypes.string,
  orderId: PropTypes.any,
  size: PropTypes.string,
};
